import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Week 13, constructing logic circuits in mammalian cells",
  "description": "",
  "published": "2014-01-23T00:00:00.000Z",
  "redirect_from": ["/blog/week-13-constructing-logic-circuits-in-mammalian-cells/swizec/6492"],
  "categories": "52papers52weeks, DNA-binding domain, Learning, Logic gate, NOR gate, Personal, Technology",
  "hero": "./img/images-7919.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><em parentName="p">{`[`}{`This post is part of an ongoing challenge to understand 52 papers in 52 weeks. You can read previous entries, `}<a parentName="em" {...{
          "href": "http://swizec.com/blog/category/52-papers-in-52-weeks"
        }}>{`here`}</a>{`, or subscribe to be notified of new posts by `}<a parentName="em" {...{
          "href": "http://swiz.ec/52papers-list"
        }}>{`email`}</a>{`]`}</em></p>
    <p>{`One of my friends is doing a PhD on implementing logic circuits in mammalian cells. Just thinking about it tickles my nerdy innards!`}</p>
    <p>{`After `}<em parentName="p">{`months`}</em>{` of begging, she let me read her latest paper after it was published in Nature last week. `}<em parentName="p"><a parentName="em" {...{
          "href": "http://www.nature.com/nchembio/journal/vaop/ncurrent/full/nchembio.1433.html"
        }}>{`Designable DNA-binding domains enable construction of logic circuits in mammalian cells`}</a></em>{` by Tina Lebar and some other people nobody cares about. One of them was my information theory professor. Small world ...`}</p>
    <p>{`I chewed my way through through this five-page paper like an excited puppy goes through a slipper and just like a puppy I was confused out of my mind most of the time. Synthetic biology is `}<em parentName="p">{`not`}</em>{` my thing.`}</p>
    <h2 {...{
      "id": "introduction"
    }}>{`Introduction`}</h2>
    <p>{`I asked my friend about her research a few months ago and she explained that programmable cells can be very useful. You could for instance program a cell to deliver medicine only to specific points in the body. Or to attack only certain types of other cells and so on.`}</p>
    <p>{`This, apparently, has far reaching implications in medicine. Personally, I'm just waiting until we can build cylons.`}</p>
    <p>{`What I don't understand, and this paper doesn't explain, is `}<em parentName="p">{`how`}</em>{` all this works. The paper talks about designing different types of logic gates, assembling them into circuits, but I don't understand the basic principles. It's electron quantum tunneling through layers of silicone for normal computers, but for biological circuits it's ... magic?`}</p>
    <p><img parentName="p" {...{
        "src": "/529c4f6ad7cc613871ecaec87e24413e/images-7835.gif",
        "alt": "Biological circuits"
      }}></img></p>
    <p>{`The paper uses this magic to build different logic gates. NOR and NAND in particular because they are functionally complete and can be used to implement anything. You could build a whole computer just by combining NOR or `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/NAND_gate",
        "title": "NAND gate"
      }}>{`NAND gates`}</a>{`.`}</p>
    <p>{`Computers used to be built like this in the 1960's (Apollo computer was just a combination of triple-input NOR gates). These days we instead try to lower the number of elements to save power because the gates themselves have become so cheap to make.`}</p>
    <p>{`But biologic chips are in their infancy and using easily replicable elements is more important. As far as I understand, this paper is the first to implement a circuit out of multiple gates.`}</p>
    <p>{`One of the problems they solved is the free diffusion in cells. They built orthogonal gates that behave correctly even when activation sequences act funny.`}</p>
    <h2 {...{
      "id": "orthogonal-nor-gates"
    }}>{`Orthogonal NOR gates`}</h2>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/e382cd02094fe7e9677f596aa503d88f/1e5d2/images-7919.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "59.19282511210761%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'236\\'%20viewBox=\\'0%200%20400%20236\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M203%2013c-4%204%200%208%207%208%205%200%207-1%207-4l-1-4h-13m110%201l-1%205c0%202%201%203%208%203h7v-9h-6l-8%201M207%2044v13h6V30h-6v14m90%201v13h5V31h-5v14M136%2064c-2%200-2%200-2%2018v17h8V82c0-19%200-19-6-18M49%2082v17h8V64h-8v18m99%200v17h8V64h-8v18m-13%200v16h6V66h-6v16M41%2071c-2%200-2%202-2%2014v14h8l-1-14c0-15%200-15-5-14m-1%2015v12h5V74h-5v12m158%2052v28h27v-56h-27v28m28%200v28h6l13-1h8v-55h-27v28m57-12v41h55v-55h-55v14m56%2014v27h28v-55h-28v28m29%200v27h27v-55h-27v28m-247%2027v3h20c22%200%2023%200%2021-5%200-2-1-2-21-2h-20v4m0%2010v3h9c10%200%2011-1%2010-5%200-2-1-2-10-2h-9v4\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/e382cd02094fe7e9677f596aa503d88f/ca0a1/images-7919.webp 223w", "/static/e382cd02094fe7e9677f596aa503d88f/75680/images-7919.webp 445w", "/static/e382cd02094fe7e9677f596aa503d88f/8d1ba/images-7919.webp 890w", "/static/e382cd02094fe7e9677f596aa503d88f/3838e/images-7919.webp 1335w", "/static/e382cd02094fe7e9677f596aa503d88f/ba360/images-7919.webp 1630w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/e382cd02094fe7e9677f596aa503d88f/e92b6/images-7919.png 223w", "/static/e382cd02094fe7e9677f596aa503d88f/e66bf/images-7919.png 445w", "/static/e382cd02094fe7e9677f596aa503d88f/4ef49/images-7919.png 890w", "/static/e382cd02094fe7e9677f596aa503d88f/4e814/images-7919.png 1335w", "/static/e382cd02094fe7e9677f596aa503d88f/1e5d2/images-7919.png 1630w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/e382cd02094fe7e9677f596aa503d88f/4ef49/images-7919.png",
                "alt": "Design of single-layer genetic NOR gates",
                "title": "Design of single-layer genetic NOR gates",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`Design of single-layer genetic NOR gates`}</figcaption>{`
  `}</figure></p>
    <p>{`To build layered circuits inputs have to be the same type as outputs. The authors used TALEs (transcriptor activator-like effects) as information mediators and designable DNA-binding domains as the logic circuits themselves.`}</p>
    <p>{`The first step towards a complex circuit was implementing a single `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/NOR_gate",
        "title": "NOR gate"
      }}>{`NOR gate`}</a>{` as an OR-`}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Inverter_%28logic_gate%29",
        "title": "Inverter (logic gate)"
      }}>{`NOT gate`}</a>{` with three layers - two layers for OR and an extra inverter to make a NOR.`}</p>
    <p>{`Free diffusion in cells is the biggest problem here. How do you ensure a gate works regardless of activation order of binding sites?`}</p>
    <p>{`They solved this by positioning TALE binding sites upstream of something called a constitutive mammalian promoter (CMV), which leads to equal promoter activation regardless of binding sequence variability.`}</p>
    <p>{`To make a simple `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/OR_gate",
        "title": "OR gate"
      }}>{`OR gate`}</a>{` they place two binding sites for different TALE repressors before the `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Promoter_%28genetics%29",
        "title": "Promoter (genetics)"
      }}>{`constitutive promoter`}</a>{` followed by an expressor gene. To turn it into a NOR gate, they added another binding site next to the other two.`}</p>
    <p>{`Results were checked using luminometry and as you can see in the picture above, cells only light up when neither TALE repressor is expressed in the cell. Shiny.`}</p>
    <p>{`To demonstrate the generality of this design they implemented several different NOR gates that used different TALE repressors in various combinations. They took the experiments even further by implementing parallel NOR gates acting on different expressors within the same cell.`}</p>
    <p>{`All of this means the gate design is orthogonal and canin fact be used to implement more complex circuits.`}</p>
    <h2 {...{
      "id": "two-input-logic-gates"
    }}>{`Two-input logic gates`}</h2>
    <p>{`The real test of orthogonality comes when you want to implement a multi-layered `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Logic_gate",
        "title": "Logic gate"
      }}>{`logic circuit`}</a>{` - an `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/XOR_gate",
        "title": "XOR gate"
      }}>{`XOR gate`}</a>{`.`}</p>
    <p>{`The idea is to use the expression of a TALE repressor in one NOR gate as an input to the next. This way you can implement any logic function you can think of just by layering NOR gates in specific configurations.`}</p>
    <p>{`All 16 two-input functions were implemented both in separate cell colonies and within the same cell, and a new experiment was performed for each combination of inputs. According to flow cytometry and microscopy, all biologic functions produced high and low outputs in accordance with their truth tables. But it did sometimes take a while for circuits to equilibrize before reaching correct output.`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/d9cb8d0ae7ed26db981a8d0d40a11e40/098c1/images-7992.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "58.744394618834086%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'234\\'%20viewBox=\\'0%200%20400%20234\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M189%2047v25h51V21h-51v26m52%200v25h51V22l-26-1h-25v26m130%200v23h-6V27h-8v43h-3c-3%200-3%200-3-2l-1-3-2-1h-2l-2%201-1%202c0%202-1%203-3%203-1%201%2010%201%2026%201l29%201h1l-1-2-2-1-4-1-4%201-4%201h-2V23h-8v24M62%2043c-3%202-3%208%200%209%203%202%2011%201%2012-1%205-4%201-9-6-9l-6%201m127%2056l1%2026h50V74h-51v25m52%201v25h51V74h-51v26M61%2096l-1%204c0%203%201%204%207%205%203%201%203%201%203%207v5h-9l-10-1%202-2%201-5v-5h-8v5l1%205%202%202%201%203h37c37%200%2038%200%2038-2l1-2c1-1%200-1-1-1-2%200-3%200-2%201%204%201-2%202-25%202H71v-5c0-5%200-6%203-7%202-2%203-5%201-8%200-2-13-3-14-1m279%207v7h13v-6l1-6v12h13V98h14c8%200%202-1-13-1h-28v6m-150%2024l-1%2025v25h51v-50l-25-1-25%201m51%2024v26h51v-50l-26-1h-25v25M50%20132c-1%203%200%206%201%203l7-1h7v5c0%204%200%205-2%207-6%205-3%2011%205%2011%206%200%208-1%208-5s-1-5-6-6l-4-1v-11h27c24%200%2028%200%2025%201-1%201%200%201%201%201%203%200%203%200%202-2s-2-2-36-2H50m139%2072l1%2026h50v-51h-51v25m52%201v25h51v-51h-51v26M53%20185c-2%200-3%201-3%202%200%202%200%202%201%201%200-2%201-2%207-2h7v5l-1%206c-1%201%200%201%202%201%201%200%202%200%201-1l-1-6v-5h27l26%201-2%203c-3%202-4%206-2%208h9l2%201c2%200%202%200%201-1-2-2-3-5-1-3l1-1-2-1c-2%201-2%200-2-2%200-6%200-6-35-6H53m8%2015l-1%205c0%203%201%204%204%204%205%200%206%202%206%208v5h-9l-10-1-1-1-1%202c1%202%206%202%2038%202%2037%200%2038%200%2038-2l1-2c1-1%200-1-1-1-2%200-3%200-2%201%204%201-2%202-25%202H71v-5c0-4%200-6%203-8%202-2%203-7%201-9s-12-1-14%200\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/d9cb8d0ae7ed26db981a8d0d40a11e40/ca0a1/images-7992.webp 223w", "/static/d9cb8d0ae7ed26db981a8d0d40a11e40/75680/images-7992.webp 445w", "/static/d9cb8d0ae7ed26db981a8d0d40a11e40/8d1ba/images-7992.webp 890w", "/static/d9cb8d0ae7ed26db981a8d0d40a11e40/3838e/images-7992.webp 1335w", "/static/d9cb8d0ae7ed26db981a8d0d40a11e40/6f21e/images-7992.webp 1578w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/d9cb8d0ae7ed26db981a8d0d40a11e40/e92b6/images-7992.png 223w", "/static/d9cb8d0ae7ed26db981a8d0d40a11e40/e66bf/images-7992.png 445w", "/static/d9cb8d0ae7ed26db981a8d0d40a11e40/4ef49/images-7992.png 890w", "/static/d9cb8d0ae7ed26db981a8d0d40a11e40/4e814/images-7992.png 1335w", "/static/d9cb8d0ae7ed26db981a8d0d40a11e40/098c1/images-7992.png 1578w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/d9cb8d0ae7ed26db981a8d0d40a11e40/4ef49/images-7992.png",
                "alt": "An XOR gate",
                "title": "An XOR gate",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`An XOR gate`}</figcaption>{`
  `}</figure></p>
    <p>{`To minimize the number of layers and required transcription factors, genetic algorithm optimization was applied, and it is now possible to implement all 16 two-input logic gates with just two layers. A layer less than the previous three. Which is cool.`}</p>
    <p>{`If I understand the implications correctly, that means you can put more logic into a cell and it's easier to make the whole thing.`}</p>
    <h2 {...{
      "id": "function-selecting-circuit"
    }}>{`Function selecting circuit`}</h2>
    <p>{`For circuits to be really effective, you also need the ability to select which logic function within the same circuit will be performed solely based on inputs.`}</p>
    <p>{`To demonstrate this ability, the authors implemented a simple circuit that takes three inputs and decides whether an AND or an OR function will be performed on two of the inputs, based on the third. A truthy value means OR and a falsy value means AND - essentially the `}<em parentName="p">{`majority`}</em>{` function.`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/78b624e91762e34223b5d75cbeeb41d7/03692/Majority-function23h111.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "35.874439461883405%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'144\\'%20viewBox=\\'0%200%20400%20144\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M282%2035v13h27V22h-27v13m28%200v13h27V22h-27v13m31%200v13h27V22h-27v13m28%200v13h27V22h-27v13M45%2038c0%204%200%205-2%205v1c2%200%202%201%202%208l-1%207h7c6-1%2014-3%2013-4l-8%201c-10%203-10%203-10-4%200-4%200-5%201-4%201%202%209%203%2011%201%204-5-5-10-10-5-2%201-2%201-2-4v-5l7%201c12%202%2019%206%2018%2012v2h4l2-1h-1c-2%200-4-2-2-3h2l2%201%201%2016-1%2016-1%201%201%201c1-1%201%205%201%2015l-1%2017-1%201h2l1-17c0-12%200-17%201-15l2%202%201%204v4h7c8-1%2016-4%2019-7%201-2%203-2%204-2%203%200%204-5%200-6l-3%203c-3%206-9%209-20%2011h-5V69h6l11%203c6%203%207%203%204%201s-10-5-18-5l-5-1v15l-1-3c0-3-2-4-2-1-1%201-1-6-1-15V46l-3-1-3-1-4-3c-4-3-11-6-20-7h-6l1%204m238%2012l-1%2013v13h27V49h-13l-13%201m27%2012v14h27V50l-14-1h-13v13m32-12l-1%2013v13h27V49h-13l-13%201m27%2012v14h27V50l-14-1h-13v13m-171%205v2h19l20-1c1-4%200-4-20-4h-19v3M45%2068v4c0%203%200%204-2%204v1c3%200%203%205%200%205-2%200-2%201-2%2013l-1%2015v1c3%200%201%205-2%205-1%201-1%201%202%201h5v5l-1%204h4c4%200%2014-2%2017-4h-4l-10%202-5%201v-22h4c11%200%2022%206%2021%2012v2h4l2-1-2-1-1-1%201-1%202-1-3-1-4-3c-4-3-11-6-20-7h-6l1%204-1%205c-2%200-2-3-2-15%200-10%200-12%202-12l1%205-1%204h4c5%200%2016-3%2019-5s2-2-3%200c-3%202-12%204-16%204-2%200-2-1-2-5s0-5%201-4l4%201c2%200%203%200%202-3%200-4-3-5-6-3-1%201-1%200-1-3v-5h4l10%202%206%201c-2-2-20-6-21-4m237%2026v13h27V80h-27v14m28%200v13h27V80h-27v14m31%200v13h27V80h-27v14m28%200v13h27V80h-27v14m-170%203c-2%204%200%204%2016%204h16v-2c0-2-1-3-16-3l-16%201m83%2025v13h27v-27h-27v14m28%200v13h27v-27h-27v14m31-1l1%2014h26v-27h-27v13m28%201v13h27v-27h-27v14m-170-11v2l14%201c14%200%2015%200%2015-2s0-2-14-2l-15%201m0%2014c-2%203%200%203%2020%203h19v-5h-19c-17%200-20%200-20%202\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/78b624e91762e34223b5d75cbeeb41d7/ca0a1/Majority-function23h111.webp 223w", "/static/78b624e91762e34223b5d75cbeeb41d7/75680/Majority-function23h111.webp 445w", "/static/78b624e91762e34223b5d75cbeeb41d7/8d1ba/Majority-function23h111.webp 890w", "/static/78b624e91762e34223b5d75cbeeb41d7/3838e/Majority-function23h111.webp 1335w", "/static/78b624e91762e34223b5d75cbeeb41d7/e11e5/Majority-function23h111.webp 1780w", "/static/78b624e91762e34223b5d75cbeeb41d7/d9d5d/Majority-function23h111.webp 2440w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/78b624e91762e34223b5d75cbeeb41d7/e92b6/Majority-function23h111.png 223w", "/static/78b624e91762e34223b5d75cbeeb41d7/e66bf/Majority-function23h111.png 445w", "/static/78b624e91762e34223b5d75cbeeb41d7/4ef49/Majority-function23h111.png 890w", "/static/78b624e91762e34223b5d75cbeeb41d7/4e814/Majority-function23h111.png 1335w", "/static/78b624e91762e34223b5d75cbeeb41d7/701e9/Majority-function23h111.png 1780w", "/static/78b624e91762e34223b5d75cbeeb41d7/03692/Majority-function23h111.png 2440w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/78b624e91762e34223b5d75cbeeb41d7/4ef49/Majority-function23h111.png",
                "alt": "Majority function",
                "title": "Majority function",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`Majority function`}</figcaption>{`
  `}</figure></p>
    <p>{`You can test the design out by hand, but the basic idea is that A chooses whether the first layer of NOR gates will act as an OR or an AND, and the final NOR gate activates only if the previous layer returns only low values.`}</p>
    <p>{`Once again, fluorescence reporters observed under a microscope over a few different experiments show everything works as you'd expect.`}</p>
    <h2 {...{
      "id": "discussion"
    }}>{`Discussion`}</h2>
    <p>{`With this new design, synthetic biologists can improve the predictability, reliability, and standardization of biological information processing systems. With the recent advance in producing hundreds of different TALEs this makes the design much better than previous approaches.`}</p>
    <p>{`Zinc fingers and CRISPR were mentioned here, but I only vaguely understood the zinc fingers paper when I read it a year ago and I don't know what CRISPR might be. I'm guessing it's something a bit like a TALE, but different.`}</p>
    <p>{`Either way, the authors report that using their optimizations 94% of two-input logic gates can be implemented with just two layers, whereas only 50% can be implemented in two layers with traditional wired circuits. A pretty sweet result if you ask me.`}</p>
    <p>{`They also mention something about distributed systems spanning multiple cells and a bunch of other things that read almost like wishful thinking. Maybe I'd believe them if I spent the time reading referenced papers.`}</p>
    <p>{`But this has been enough synthetic biology for now. I'm always amazed by how mind-blowing this field can be.`}</p>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/?px",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=485119f1-0ea7-4a9e-adcf-cf124e742208",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      